import React from "react";
import ReactGA from "react-ga";
import styles from "./bubbles.module.scss";
import { useIsMobile } from "../../hooks/mobile.js";
import { pack, hierarchy } from "d3-hierarchy";
import useComponentSize from "@rehooks/component-size";
import ReactTooltip from "react-tooltip";

const TOPIC_ICON_SIZE = 30;
const BUBBLES_WIDTH = 1400;
const BUBBLES_HEIGHT = 430;
const BUBBLE_MOBILE_SCALE_BACKGROUND = 0.65;
const BUBBLE_MOBILE_SCALE_BUBBLES = 0.75;
const MOBILE_BUBBLE_CUTOFF = 785;
const THEME_COLOURS = {
    "key-values": "#45bfb4",
    "innovation-and-technology": "#e88b4f",
    "management-impact": "#41ad49",
    "how-is-the-reef-used": "#6e256b",
};

ReactGA.initialize("UA-11963560-5");

export default function Bubbles(props) {
    const isMobile = useIsMobile();
    const {
        themes,
        opportunities,
        topics,
        filteredOpportunities,
        handleTopicFilter,
        handleScrollToOpportunities,
        handleReset,
        handleHideCompleted,
        hideCompleted,
        completed,
    } = props;
    const [nodes, setNodes] = React.useState(null);
    const [maxTheme, setMaxTheme] = React.useState(null);

    let bubblesRef = React.useRef(null);
    let { width } = useComponentSize(bubblesRef);

    const topicActiveWithinTheme = (topic, theme) => {
        return filteredOpportunities
            .filter(d => [d.themes.primary, d.themes.secondary].includes(theme.theme))
            .map(d => d.topic.topic)
            .includes(topic.data);
    };

    const themeActive = t => {
        const activeThemes = [
            ...new Set(filteredOpportunities.map(d => [d.themes.primary, d.themes.secondary]).flat()),
        ];
        return activeThemes.includes(t.theme);
    };

    const slugify = s => {
        return s.replace(/\s/g, "-").toLowerCase();
    };

    React.useEffect(() => {
        if (opportunities && themes) {
            // get all the topics from the children inside the topic.
            const opportunitiesHierarchy = themes.map(t => {
                return {
                    name: t.theme,
                    children: [
                        ...new Set(
                            opportunities
                                .filter(o => [o.themes.primary, o.themes.secondary].includes(t.theme))
                                .map(o => o.topic.topic)
                        ),
                    ],
                };
            });

            const hier = hierarchy({ children: opportunitiesHierarchy }).sum(d => 1);
            let bubbles = pack()
                .size([BUBBLES_WIDTH, BUBBLES_HEIGHT])
                .padding(TOPIC_ICON_SIZE / 2)
                .radius(d => TOPIC_ICON_SIZE / 2);
            const packedData = bubbles(hier);
            const customData = packedData.descendants().map(d => {
                return {
                    ...d,
                    customCoordinates: {
                        x: d.parent ? d.x - d.r - d.parent.x : d.x - d.r,
                        y: d.parent ? d.y - d.r - (d.parent.y - d.parent.r) : d.y - d.r,
                    },
                };
            });

            setNodes(customData);
            setMaxTheme(
                Math.max(
                    ...packedData
                        .descendants()
                        .filter(d => d.depth === 1)
                        .map(d => d.r)
                )
            );
        }
    }, [opportunities, themes]);

    return (
        <React.Fragment>
            {isMobile && (
                <div className={styles.bubblesHeadingOpportunities}>
                    {filteredOpportunities.length === 1
                        ? `${filteredOpportunities.length} opportunity`
                        : `All ${filteredOpportunities.length} opportunities`}
                </div>
            )}
            <div className={styles.bubbles} ref={bubblesRef}>
                {themes.map(d => {
                    return (
                        <div
                            className={styles.themeBubble}
                            key={d.slug}
                            data-active={themeActive(d) ? "active" : "inactive"}
                        >
                            <div className={styles.themeBubbleHeading}>{d.short}</div>
                            <div className={styles.themeBubbleStats}>
                                {
                                    filteredOpportunities.filter(o =>
                                        [o.themes.primary, o.themes.secondary].includes(d.theme)
                                    ).length
                                }{" "}
                                opportunities across{" "}
                                {
                                    [
                                        ...new Set(
                                            filteredOpportunities
                                                .filter(o => [o.themes.primary, o.themes.secondary].includes(d.theme))
                                                .map(o => o.topic.topic)
                                        ),
                                    ].length
                                }{" "}
                                topics
                            </div>
                            <div className={styles.themeBubbles}>
                                <div
                                    className={styles.themeBubblesBackground}
                                    data-theme={d.theme}
                                    style={{
                                        width:
                                            nodes && maxTheme
                                                ? isMobile
                                                    ? `${
                                                          (nodes.find(n => n.data.name === d.theme).r / maxTheme) *
                                                          100 *
                                                          BUBBLE_MOBILE_SCALE_BACKGROUND
                                                      }%`
                                                    : `${
                                                          (nodes.find(n => n.data.name === d.theme).r / maxTheme) * 100
                                                      }%`
                                                : 0,
                                    }}
                                ></div>
                                <div
                                    className={styles.themeBubblesNodeContainer}
                                    style={{
                                        width:
                                            nodes && maxTheme
                                                ? `${(nodes.find(n => n.data.name === d.theme).r / maxTheme) * 100}%`
                                                : 0,
                                        transform: isMobile
                                            ? `scale(${(width / MOBILE_BUBBLE_CUTOFF) * BUBBLE_MOBILE_SCALE_BUBBLES},${
                                                  (width / MOBILE_BUBBLE_CUTOFF) * BUBBLE_MOBILE_SCALE_BUBBLES
                                              })`
                                            : `scale(${width / BUBBLES_WIDTH}, ${width / BUBBLES_WIDTH})`,
                                    }}
                                >
                                    {nodes &&
                                        nodes
                                            .filter(
                                                n => n.parent && n.parent.data.name && n.parent.data.name === d.theme
                                            )
                                            .map(n => {
                                                return (
                                                    <span
                                                        className={styles.topicBubble}
                                                        key={n.data}
                                                        title={n.data}
                                                        style={{
                                                            transform: `translate(${n.customCoordinates.x}px, ${n.customCoordinates.y}px)`,
                                                        }}
                                                        onClick={() => {
                                                            if (
                                                                filteredOpportunities.filter(
                                                                    o => o.topic.topic === n.data
                                                                ).length > 0
                                                            ) {
                                                                handleTopicFilter([
                                                                    { value: slugify(n.data), label: n.data },
                                                                ]);
                                                                ReactGA.event({
                                                                    category: "SCIKNOW: bubbles",
                                                                    action: "clickTopic",
                                                                    label: n.data,
                                                                });
                                                                if (!isMobile) handleScrollToOpportunities();
                                                            }
                                                        }}
                                                        data-active={
                                                            topicActiveWithinTheme(n, d) ? "active" : "inactive"
                                                        }
                                                        data-tip={n.data}
                                                        data-for={d.theme + "-" + n.data}
                                                    >
                                                        <img
                                                            src={
                                                                "data:image/svg+xml," +
                                                                encodeURIComponent(
                                                                    topics
                                                                        .find(t => t.topic === n.data)
                                                                        .icon.replace(
                                                                            /currentColor/g,
                                                                            THEME_COLOURS[d.slug]
                                                                        )
                                                                )
                                                            }
                                                            alt={n.data}
                                                            className={styles.topicIcon}
                                                            style={{
                                                                height: TOPIC_ICON_SIZE * 0.9,
                                                                width: TOPIC_ICON_SIZE * 0.9,
                                                            }}
                                                            width={TOPIC_ICON_SIZE * 0.9}
                                                            height={TOPIC_ICON_SIZE * 0.9}
                                                            aria-hidden="true"
                                                        />
                                                    </span>
                                                );
                                            })}
                                </div>
                            </div>
                        </div>
                    );
                })}
                {themes.map(d => {
                    return (
                        <React.Fragment key={d.theme}>
                            {nodes &&
                                nodes
                                    .filter(n => n.parent && n.parent.data.name && n.parent.data.name === d.theme)
                                    .map(n => {
                                        return (
                                            <ReactTooltip
                                                aria-haspopup="true"
                                                type="light"
                                                id={d.theme + "-" + n.data}
                                                className={styles.popup}
                                                key={d.theme + "-" + n.data}
                                                effect="solid"
                                                arrowColor="#FFFFFF00"
                                            >
                                                <div className={styles.popupContainer} data-theme={d.theme}>
                                                    <div className={styles.popupTopic}>
                                                        <img
                                                            src={
                                                                "data:image/svg+xml," +
                                                                encodeURIComponent(
                                                                    topics
                                                                        .find(t => t.topic === n.data)
                                                                        .icon.replace(
                                                                            /currentColor/g,
                                                                            THEME_COLOURS[d.slug]
                                                                        )
                                                                )
                                                            }
                                                            alt={n.data}
                                                            className={styles.popupTopicIcon}
                                                            style={{
                                                                height: TOPIC_ICON_SIZE * 0.9,
                                                                width: TOPIC_ICON_SIZE * 0.9,
                                                            }}
                                                            aria-hidden="true"
                                                            height={TOPIC_ICON_SIZE * 0.9}
                                                            width={TOPIC_ICON_SIZE * 0.9}
                                                        />
                                                        <span className={styles.popupTopicTopic}>{n.data}</span>
                                                    </div>
                                                    <div className={styles.popupOpportunities}>
                                                        {
                                                            filteredOpportunities.filter(o => o.topic.topic === n.data)
                                                                .length
                                                        }{" "}
                                                        {filteredOpportunities.filter(o => o.topic.topic === n.data)
                                                            .length === 1
                                                            ? " opportunity with selected filters/search"
                                                            : " opportunities with selected filters/search"}
                                                    </div>
                                                    {filteredOpportunities.filter(o => o.topic.topic === n.data)
                                                        .length > 0 && (
                                                        <small className={styles.popupFilter}>
                                                            Click to add this topic to filters and see opportunities.
                                                        </small>
                                                    )}
                                                </div>
                                            </ReactTooltip>
                                        );
                                    })}
                        </React.Fragment>
                    );
                })}
                {filteredOpportunities.length === 0 && (
                    <div className={styles.noResultsContainer}>
                        <div className={styles.noResults}>
                            Sorry there are no results, please{" "}
                            <button onClick={handleReset} className={styles.noResultsReset}>
                                reset filters and search
                            </button>{" "}
                            to try again.
                            {completed > 0 && hideCompleted && (
                                <div className={styles.completed}>
                                    There {completed === 1 ? "is " : "are "} {completed} completed{" "}
                                    {completed === 1 ? " opportunity " : " opportunities"} covered by the filters.
                                    <button onClick={handleHideCompleted} className={styles.showCompleted}>
                                        Show completed opportunities
                                    </button>{" "}
                                    to see {completed === 1 ? "it" : "them"}.
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </React.Fragment>
    );
}
